import { __ } from "@wordpress/i18n";
import { 
  useBlockProps, 
  InspectorControls, 
  RichText,
  InnerBlocks,
  useInnerBlocksProps
} from "@wordpress/block-editor";
import { 
  PanelBody, 
  TextControl, 
  TextareaControl, 
  Button, 
  Notice,
  TabPanel
} from "@wordpress/components";
import { useState } from "@wordpress/element";
import { createBlock } from "@wordpress/blocks";

export default function Edit({ attributes, setAttributes, clientId }) {
  const blockProps = useBlockProps({ className: "what-to-expect" });
  
  const { mainTitle, introHeading, stepsHeading, stepsIntro, steps } = attributes;
  const [activeStepTab, setActiveStepTab] = useState(0);

  const addStep = () => {
    const newStep = {
      stepName: `Step ${steps.length + 1}`,
      subtitle: "New Step",
      text: "", // Add empty text field to default to legacy mode
      ctaText: "Learn More",
      ctaUrl: "#"
    };
    setAttributes({ steps: [...steps, newStep] });
    // Switch to the new step
    setActiveStepTab(steps.length);
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    setAttributes({ steps: newSteps });
    // If we removed the active tab, reset to first tab
    if (activeStepTab >= newSteps.length) {
      setActiveStepTab(0);
    }
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setAttributes({ steps: newSteps });
  };

  const migrateStepToInnerBlocks = (index) => {
    const step = steps[index];
    if (!step.text) return;

    // Split text into paragraphs
    const paragraphs = step.text
      .split(/\n\s*\n/) // Split by double newlines first
      .map(p => p.trim())
      .filter(p => p.length > 0);

    // If no double newlines found, split by single newlines
    const finalParagraphs = paragraphs.length === 1 && paragraphs[0].includes('\n')
      ? paragraphs[0].split('\n').map(p => p.trim()).filter(p => p.length > 0)
      : paragraphs;

    // Create paragraph blocks
    const paragraphBlocks = finalParagraphs.map(paragraphText => 
      createBlock('core/paragraph', {
        content: paragraphText
      })
    );

    // Update step: clear text and set inner content
    const newSteps = [...steps];
    newSteps[index] = { 
      ...newSteps[index], 
      text: '', // Clear legacy text
      innerContent: paragraphBlocks // Set new blocks
    };
    
    setAttributes({ steps: newSteps });
    
    // Switch to the migrated step in the editor
    setActiveStepTab(index);
  };

  const updateStepInnerContent = (index, blocks) => {
    const newSteps = [...steps];
    newSteps[index] = { 
      ...newSteps[index], 
      innerContent: blocks 
    };
    setAttributes({ steps: newSteps });
  };

  // Create inner blocks template for each step with default content
  const getStepTemplate = (stepIndex) => [
    ['core/paragraph', { 
      content: steps[stepIndex]?.defaultContent || 'Add content for this step...',
      placeholder: 'Add content for this step...'
    }]
  ];

  // Get unique client IDs for each step's inner blocks
  const getStepClientId = (stepIndex) => `${clientId}-step-${stepIndex}`;

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Content Settings', 'puroclean')} initialOpen={true}>
          <TextControl
            label={__('Main Title', 'puroclean')}
            value={mainTitle}
            onChange={(value) => setAttributes({ mainTitle: value })}
            help={__('The main heading for the section', 'puroclean')}
          />
          <TextareaControl
            label={__('Intro Heading (H2)', 'puroclean')}
            value={introHeading}
            onChange={(value) => setAttributes({ introHeading: value })}
            rows={4}
            help={__('The introductory text that appears below the main title', 'puroclean')}
          />
          <TextControl
            label={__('Steps Section Heading (H3)', 'puroclean')}
            value={stepsHeading}
            onChange={(value) => setAttributes({ stepsHeading: value })}
            help={__('The heading for the steps section', 'puroclean')}
          />
          <TextareaControl
            label={__('Steps Introduction', 'puroclean')}
            value={stepsIntro}
            onChange={(value) => setAttributes({ stepsIntro: value })}
            rows={3}
            help={__('Text that appears before the steps', 'puroclean')}
          />
        </PanelBody>

        <PanelBody title={__('Steps Management', 'puroclean')} initialOpen={false}>
          <Notice status="info" isDismissible={false}>
            <p>
              <strong>Manage Steps:</strong> Add, remove, and edit steps below. Each step can contain any blocks (paragraphs, images, buttons, etc.).
            </p>
          </Notice>
          
          {steps.map((step, index) => (
            <div key={index} style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '5px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong>Step {index + 1}</strong>
                <Button 
                  isDestructive 
                  isSmall 
                  onClick={() => removeStep(index)}
                  disabled={steps.length <= 1}
                >
                  Remove
                </Button>
              </div>
              
              <TextControl
                label={__('Step Name', 'puroclean')}
                value={step.stepName}
                onChange={(value) => updateStep(index, 'stepName', value)}
              />
              <TextControl
                label={__('Subtitle', 'puroclean')}
                value={step.subtitle}
                onChange={(value) => updateStep(index, 'subtitle', value)}
              />
              
              {/* Legacy text field - show if step has text field (even if empty) or no innerContent */}
              {(step.text !== undefined || (!step.innerContent || step.innerContent.length === 0)) && (
                <>
                  <TextareaControl
                    label={__('Text (Legacy Format)', 'puroclean')}
                    value={step.text || ''}
                    onChange={(value) => updateStep(index, 'text', value)}
                    rows={3}
                    help={__('This step uses the legacy text format. You can migrate to inner blocks by clicking "Switch to Blocks Mode" below.', 'puroclean')}
                  />
                  {step.text && (
                    <Button 
                      isSecondary 
                      isSmall 
                      onClick={() => migrateStepToInnerBlocks(index)}
                      style={{ marginBottom: '10px' }}
                    >
                      {__('Switch to Blocks Mode', 'puroclean')}
                    </Button>
                  )}
                </>
              )}
              
              {/* Show switch to legacy button if in blocks mode */}
              {step.innerContent && step.innerContent.length > 0 && step.text === undefined && (
                <Button 
                  isSecondary 
                  isSmall 
                  onClick={() => {
                    const newSteps = [...steps];
                    newSteps[index] = {
                      ...newSteps[index],
                      text: '',
                      innerContent: undefined
                    };
                    setAttributes({ steps: newSteps });
                  }}
                  style={{ marginBottom: '10px' }}
                >
                  {__('Switch to Legacy Text Mode', 'puroclean')}
                </Button>
              )}
              
              <TextControl
                label={__('CTA Text', 'puroclean')}
                value={step.ctaText}
                onChange={(value) => updateStep(index, 'ctaText', value)}
              />
              <TextControl
                label={__('CTA URL', 'puroclean')}
                value={step.ctaUrl}
                onChange={(value) => updateStep(index, 'ctaUrl', value)}
              />
            </div>
          ))}
          
          <Button isPrimary onClick={addStep}>
            Add New Step
          </Button>
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        {/* Main Title */}
        <div className="bg-primary text-white py-16">
          <div className="container max-w-content mx-auto px-4">
            <RichText
              tagName="h1"
              placeholder={__('Main title...', 'puroclean')}
              value={mainTitle}
              onChange={(value) => setAttributes({ mainTitle: value })}
              className="text-4xl font-bold text-center mb-8"
            />
            
            <RichText
              tagName="h2"
              placeholder={__('Intro heading...', 'puroclean')}
              value={introHeading}
              onChange={(value) => setAttributes({ introHeading: value })}
              className="text-lg leading-relaxed max-w-4xl mx-auto text-center"
            />
          </div>
        </div>

        {/* Steps Section */}
        <div className="bg-white py-16">
          <div className="container max-w-content mx-auto px-4">
            <RichText
              tagName="h3"
              placeholder={__('Steps heading...', 'puroclean')}
              value={stepsHeading}
              onChange={(value) => setAttributes({ stepsHeading: value })}
              className="text-3xl font-bold text-center mb-6"
            />
            
            <RichText
              tagName="p"
              placeholder={__('Steps introduction...', 'puroclean')}
              value={stepsIntro}
              onChange={(value) => setAttributes({ stepsIntro: value })}
              className="text-lg leading-relaxed max-w-4xl mx-auto text-center mb-12"
            />

            {/* Steps Content Editor */}
            {steps.length > 0 && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold mb-2">Edit Step Content:</h4>
                  
                  {/* Tab Navigation for Steps */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {steps.map((step, index) => (
                      <button
                        key={index}
                        className={`px-4 py-2 rounded font-medium transition-colors ${
                          activeStepTab === index 
                            ? 'bg-primary text-white' 
                            : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                        onClick={() => setActiveStepTab(index)}
                      >
                        {step.stepName} {step.text !== undefined ? '(Legacy)' : '(Blocks)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Active Step Content Editor */}
                {steps[activeStepTab] && (
                  <div className="bg-white p-6 rounded border">
                    <div className="mb-4">
                      <h5 className="text-xl font-bold text-primary mb-2">
                        {steps[activeStepTab].stepName}: {steps[activeStepTab].subtitle}
                      </h5>
                      
                      {/* Show different content based on format */}
                      {steps[activeStepTab].text !== undefined ? (
                        <div>
                          <p className="text-sm text-orange-600 mb-4 bg-orange-50 p-3 rounded">
                            <strong>Legacy Format:</strong> This step uses the old text format. 
                            Content is managed in the sidebar under "Steps Management". 
                            Click "Migrate to Inner Blocks" to use the new block editor.
                          </p>
                          <div className="bg-gray-50 p-4 rounded">
                            <h6 className="font-semibold mb-2">Current Text Content:</h6>
                            <div className="text-sm text-gray-700 whitespace-pre-wrap">
                              {steps[activeStepTab].text || '(Empty - add content in sidebar)'}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-green-600 mb-4 bg-green-50 p-3 rounded">
                            <strong>Block Format:</strong> Add any blocks you want for this step content 
                            (paragraphs, images, buttons, etc.)
                          </p>
                          
                          {/* Inner Blocks for the active step */}
                          <div className="prose max-w-none">
                            <InnerBlocks
                              allowedBlocks={[
                                'core/paragraph',
                                'core/heading',
                                'core/image',
                                'core/list',
                                'core/button',
                                'core/buttons',
                                'core/quote',
                                'core/embed',
                                'core/html'
                              ]}
                              template={steps[activeStepTab]?.innerContent ? undefined : getStepTemplate(activeStepTab)}
                              templateLock={false}
                              orientation="vertical"
                              value={steps[activeStepTab]?.innerContent || []}
                              onInput={(blocks) => updateStepInnerContent(activeStepTab, blocks)}
                              onChange={(blocks) => updateStepInnerContent(activeStepTab, blocks)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                    
                    {/* CTA Preview */}
                    {steps[activeStepTab].ctaText && (
                      <div className="mt-6 pt-4 border-t">
                        <a 
                          href="#" 
                          className="text-primary underline font-semibold"
                          onClick={(e) => e.preventDefault()}
                        >
                          {steps[activeStepTab].ctaText}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Preview of stepper interface */}
            <div className="mt-8 bg-gray-100 p-6 rounded-lg">
              <h4 className="text-lg font-semibold mb-4">Frontend Preview:</h4>
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Step Navigation Preview */}
                  <div className="lg:w-1/4">
                    <div className="space-y-2">
                      {steps.map((step, index) => (
                        <div 
                          key={index}
                          className={`p-3 rounded text-center font-semibold ${
                            index === 0 ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {step.stepName}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Active Step Content Preview */}
                  <div className="lg:w-3/4">
                    <h4 className="text-2xl font-bold text-primary mb-4">
                      {steps[0]?.subtitle || 'Step Title'}
                    </h4>
                    <div className="text-gray-600 mb-4">
                      Content will be rendered from inner blocks for each step
                    </div>
                    {steps[0]?.ctaText && (
                      <a 
                        href="#" 
                        className="text-primary underline font-semibold"
                        onClick={(e) => e.preventDefault()}
                      >
                        {steps[0].ctaText}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}